import type { AppDescription } from "@glide/app-description";
import { AppKind } from "@glide/location-common";
import {
    type NativeTableName,
    type TableName,
    areTableNamesEqual,
    isFavoritedColumnName,
    isTableName,
    type TableColumn,
    type TableGlideType,
    type TypeSchema,
    getTableColumn,
    getTableName,
} from "@glide/type-schema";
import { memoizeFunction } from "@glide/support";
import type { AppDescriptionContext } from "@glide/function-utils";
import { DefaultMap, assert, defined, mapFilterUndefined } from "@glideapps/ts-necessities";
import { CollectingVisitor, SearchResultCollector } from "./components/find-column-uses";
import { walkAppDescription } from "./components/walk-app-description";
import { getColumnsUsedInColumn } from "./computed-columns";

function getAllFavoritesTables(adc: AppDescriptionContext): ReadonlySet<TableGlideType> {
    // Pages don't support favorites, but we can still get into
    // configurations where they're used, in particular when we
    // auto-generate column assignments.
    if (adc.appKind === AppKind.Page) return new Set();

    const tablesWithFavorites = new Set<TableGlideType>();

    class FindColumnUsesVisitor extends CollectingVisitor {
        protected shouldAddColumnUsed(tn: TableName, cn: string, isWrite: boolean): boolean {
            if (cn !== isFavoritedColumnName) return false;

            // Writing doesn't count, and neither does reading for a column
            // assignment.  It's easy to get into a configuration where the
            // favorites column is written to in some column assignment, with
            // the RHS being the current row's isFavorited column, because
            // those are auto-generated, but it's not actually read anywhere
            // that's visible to the user.
            if (isWrite) return false;

            const table = adc.findTable(tn);
            if (table !== undefined) {
                tablesWithFavorites.add(table);
                return true;
            }

            return false;
        }
    }

    const collector = new SearchResultCollector();
    const visitor = new FindColumnUsesVisitor(adc, collector, undefined);
    walkAppDescription(adc, visitor, []);

    return tablesWithFavorites;
}

let lastAppDescription: AppDescription | undefined;
let lastSchema: TypeSchema | undefined;
let lastFavoritesPivotNames: ReadonlySet<TableGlideType> | false | undefined;
let lastFavoritesTableNames: readonly TableName[] | undefined;

const getAllFavoritesPivotNames = memoizeFunction(
    "getAllFavoritesPivotNames",
    (adc: AppDescriptionContext): ReadonlySet<TableGlideType> => {
        const { appDescription, schema } = adc;
        if (appDescription === undefined) return new Set();

        if (lastAppDescription === appDescription && lastSchema === schema && lastFavoritesPivotNames !== undefined) {
            if (lastFavoritesPivotNames === false) return new Set();
            return lastFavoritesPivotNames;
        }

        lastAppDescription = appDescription;
        lastSchema = schema;
        lastFavoritesPivotNames = false;
        lastFavoritesTableNames = undefined;

        lastFavoritesPivotNames = getAllFavoritesTables(adc);
        lastFavoritesTableNames = Array.from(lastFavoritesPivotNames).map(getTableName);

        return lastFavoritesPivotNames;
    }
);

function getAllFavoritesTableNames(adc: AppDescriptionContext | undefined): readonly TableName[] {
    if (adc?.appDescription === undefined) return [];

    getAllFavoritesPivotNames(adc);

    return lastFavoritesTableNames ?? [];
}

export interface ColumnsShown {
    isColumnShown(table: TableGlideType, column: TableColumn, withProtected: boolean): boolean;
}

function getColumnsShownForAppUnmemoized(adc: AppDescriptionContext | undefined): ColumnsShown {
    // this is expensive to compute, so do it lazily
    let favoritesTableNames: readonly TableName[] | undefined;
    function getFavoritesTableNames() {
        if (favoritesTableNames === undefined) {
            favoritesTableNames = getAllFavoritesTableNames(adc);
        }
        return favoritesTableNames;
    }

    // Don't show columns from native tables in Glide that directly or
    // indirectly refer to native tables that aren't in the app.
    let nativeTableColumnsToExclude: Set<TableColumn> | undefined;
    function getNativeTableColumnsToExclude() {
        if (nativeTableColumnsToExclude !== undefined) {
            return nativeTableColumnsToExclude;
        }

        nativeTableColumnsToExclude = new Set();
        if (adc === undefined) {
            return nativeTableColumnsToExclude;
        }
        const { schema } = adc;

        const workQueue: TableColumn[] = [];

        const inputColumnsOfColumn = new Map<TableColumn, Set<TableColumn>>();
        const inputTablesOfColumn = new Map<TableColumn, readonly NativeTableName[]>();
        const outputsOfColumn = new DefaultMap<TableColumn, Set<TableColumn>>(() => new Set());

        // for all computed columns from native tables:
        for (const table of schema.tables) {
            for (const column of table.columns) {
                if (column.fromNativeTable !== true) continue;

                // get all columns and tables it refers to
                const inputs = getColumnsUsedInColumn(adc, getTableName(table), column);

                const inputColumns = new Set<TableColumn>();
                for (const [t, cns] of inputs[0]) {
                    for (const cn of cns) {
                        const c = getTableColumn(t, cn);
                        if (c === undefined) continue;
                        if (c.fromNativeTable !== true) continue;
                        inputColumns.add(c);
                        outputsOfColumn.get(c).add(column);
                    }
                }
                inputColumnsOfColumn.set(column, inputColumns);
                inputTablesOfColumn.set(
                    column,
                    mapFilterUndefined(inputs[1], tn => (isTableName(tn) ? undefined : tn))
                );

                // put it on the work queue
                workQueue.push(column);
            }
        }

        // for each column in the work queue:
        for (;;) {
            const column = workQueue.pop();
            if (column === undefined) break;

            // assert it's from a native table
            assert(column.fromNativeTable === true);

            // if it's already excluded, continue
            if (nativeTableColumnsToExclude.has(column)) continue;

            // if none of the columns it refers to are excluded, and all the
            // native tables it refers to are present, continue
            const inputColumns = defined(inputColumnsOfColumn.get(column));
            const inputTables = defined(inputTablesOfColumn.get(column));
            if (
                !Array.from(inputColumns).some(c => nativeTableColumnsToExclude?.has(c)) &&
                inputTables.every(tn => adc.findTable(tn) !== undefined)
            )
                continue;

            // otherwise:
            // mark it as excluded
            nativeTableColumnsToExclude.add(column);

            // push all native columns referring to it on the queue
            workQueue.push(...outputsOfColumn.get(column));
        }

        return nativeTableColumnsToExclude;
    }

    return {
        isColumnShown: (t, c, withProtected) => {
            // hidden columns are not shown
            if (c.hidden === true) return false;
            // protected columns are only shown for the purpose of writing to
            // or when we want to allow the user to un-protected them
            if (c.isProtected === true && !withProtected) return false;
            // if the column is from a native table, check if we need to
            // exclude it
            if (c.fromNativeTable === true && getNativeTableColumnsToExclude().has(c)) return false;
            // otherwise all columns except the is-favorited column are shown
            if (c.name !== isFavoritedColumnName) return true;

            // the is-favorited column is only shown if it's used in the app
            const tn = getTableName(t);
            return getFavoritesTableNames().some(n => areTableNamesEqual(n, tn));
        },
    };
}

export const getColumnsShownForApp = memoizeFunction("getColumnsShownForApp", getColumnsShownForAppUnmemoized);
